/**
 * MaintenanceTable — shared table for Batteries + Other tabs.
 * Includes Clear button per row.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';

function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const MaintenanceTable = ({ data, onClear, clearing, sortColumn, sortOrder, onSort }) => {
    useTick(1000);

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '—';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const renderResidents = (row) => {
        const residents = row.ResidentsObject;
        if (!residents || !Array.isArray(residents) || residents.length === 0) return '—';
        return residents.map((r, i) => (
            <span key={i}>
                {r.id ? (
                    <Link to={`/profiles/resident/${r.id}`} className="ar-link">{r.fName || ''} {r.lName || ''}</Link>
                ) : (`${r.fName || ''} ${r.lName || ''}`)}
                {i < residents.length - 1 ? ', ' : ''}
            </span>
        ));
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('EventDateTime')}>Event Time{sortIcon('EventDateTime')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('AlertType')}>Category{sortIcon('AlertType')}</th>
                        <th className="dp-th">Residents</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th">Location / Area</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('Status')}>Status{sortIcon('Status')}</th>
                        <th className="dp-th" style={{ width: 80 }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={8}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">✓</div>
                                    <div className="na-empty__text">No alerts in this category</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => (
                        <tr key={row.AlarmId} className="dp-tr na-row-border" style={{ borderLeftColor: row.AlertType === 'Emergency' ? '#e63757' : '#ffc107' }}>
                            <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.EventDateTime)}</td>
                            <td className="dp-td"><span className="na-duration">{durationToNow(row.EventDateTime)}</span></td>
                            <td className="dp-td">
                                <AlertTypeBadge alertType={row.AlertType} alertSubType={row.AlertSubType} />
                            </td>
                            <td className="dp-td">{renderResidents(row)}</td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    <span>{row.DeviceUse || '—'}</span>
                                    <span className="na-stack__sub">{row.SerialNumber || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    <span>{row.LocationName || '—'}</span>
                                    <span className="na-stack__sub">{row.LocationArea || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td"><AlertStatusBadge status={row.Status} /></td>
                            <td className="dp-td">
                                <button className="na-clear-btn" onClick={() => onClear(row.AlarmId)} disabled={clearing === row.AlarmId}>
                                    {clearing === row.AlarmId ? '...' : 'Clear'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default MaintenanceTable;
